import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui';
import { dateFilter, optionsFilter, vendorFilter } from '../../../components/common/ListPage/listFilters';
import { Cell, ListPage } from '../../../components/common/ListPage/ListPage';
import { StatusBadge } from '../../../components/common/Parts/Parts';
import { labelOf } from '../../../utils/labels';
import { PAYMENT_STATUS, RECORD_STATUS } from '../../../constants/status';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';

const TABS = [
  { value: 'active', label: 'Active' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: '', label: 'All' },
];

export default function PurchaseList() {
  const navigate = useNavigate();
  const add = <Button leftIcon="plus" onClick={() => navigate('/purchases/new')}>New purchase</Button>;
  return (
    <ListPage
      title="Purchases"
      subtitle="Material bought from suppliers. Saving a purchase adds it to stock."
      path="/purchases"
      tabs={TABS}
      filters={[
        dateFilter('Bill date'),
        vendorFilter,
        optionsFilter('paymentStatus', 'Payment', PAYMENT_STATUS, 'rupee'),
        { key: 'purpose', label: 'Bought for', icon: 'box', options: [{ value: 'stock', label: 'Stock' }, { value: 'for_service', label: 'A job' }] },
      ]}
      searchPlaceholder="Purchase no, bill no, supplier"
      actions={add}
      rowTo={(p) => `/purchases/${p._id}`}
      rowActions={(p) =>
        [
          { label: 'View', icon: 'eye', onClick: () => navigate(`/purchases/${p._id}`) },
          p.status === 'active' && { label: 'Edit details', icon: 'edit', onClick: () => navigate(`/purchases/${p._id}/edit`) },
          p.status === 'active' && p.balanceAmount > 0 && { label: 'Pay supplier', icon: 'rupee', onClick: () => navigate(`/transactions/new?category=purchase_payment&purchaseId=${p._id}`) },
          { label: 'Open supplier', icon: 'truck', onClick: () => navigate(`/vendors/${p.vendorId}`) },
        ].filter(Boolean)
      }
      columns={[
        { header: 'Purchase no.', sort: 'purchaseNo', sortDesc: true, render: (p) => p.purchaseNo, csv: (p) => p.purchaseNo },
        { header: 'Supplier', sort: 'vendor.name', render: (p) => <Cell title={p.vendor?.name} sub={p.vendorBillNo ? `Bill ${p.vendorBillNo}` : undefined} />, csv: (p) => p.vendor?.name },
        { header: 'Bill no.', sort: 'vendorBillNo', render: (p) => p.vendorBillNo || '-', csv: (p) => p.vendorBillNo, defaultHidden: true },
        { header: 'Bill date', sort: 'billDate', sortDesc: true, render: (p) => formatDate(p.billDate), csv: (p) => formatDate(p.billDate) },
        { header: 'For', sort: 'purpose', render: (p) => (p.purpose === 'for_service' ? 'A job' : 'Stock'), csv: (p) => p.purpose },
        {
          header: 'Payment', sort: 'paymentStatus',
          render: (p) => (p.status === 'active' ? <StatusBadge map={PAYMENT_STATUS} value={p.paymentStatus} /> : <StatusBadge map={RECORD_STATUS} value={p.status} />),
          csv: (p) => (p.status === 'active' ? labelOf(PAYMENT_STATUS, p.paymentStatus) : 'Cancelled'),
        },
        { header: 'Total', sort: 'grandTotal', sortDesc: true, align: 'right', render: (p) => <span className="font-semibold text-navy-900">{money(p.grandTotal)}</span>, csv: (p) => p.grandTotal / 100 },
        { header: 'Balance', sort: 'balanceAmount', sortDesc: true, align: 'right', render: (p) => (p.balanceAmount > 0 ? <span className="font-medium text-red-600">{money(p.balanceAmount)}</span> : <span className="text-ink-300">-</span>), csv: (p) => (p.balanceAmount ?? 0) / 100 },
      ]}
      mobile={(p) => ({
        title: p.vendor?.name,
        subtitle: [p.purchaseNo, formatDate(p.billDate)].join(' · '),
        right: money(p.grandTotal),
        badge: p.status === 'active' ? <StatusBadge map={PAYMENT_STATUS} value={p.paymentStatus} /> : <StatusBadge map={RECORD_STATUS} value={p.status} />,
      })}
      empty={{ icon: 'cart', title: 'No purchases yet', message: 'Enter supplier bills here to add material to stock.', action: add }}
    />
  );
}
